import { Alert, Button } from '@mui/material';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	CssBaseline,
	Fab,
	FormControl,
	List,
	MenuItem,
	Select,
	SelectChangeEvent,
	Typography,
} from '@mui/material';
import { useEffect, useMemo, useContext, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import { Publication } from '../../components/publication.component';
import { useNavigate } from 'react-router-dom';
import {
	CambiappContext,
	CambiappContextType,
} from '../../context/cambiapp.context';
import theme from '../../theme/mui-theme';
import { ConfirmedPublication } from '../../components/confirmed-publication.component';
import Table from '../../components/table/table-component';
import { completedPublicationsTableHeader } from '../../components/table/table-config';
import { ExpiredTableComponent } from '../../components/table/expired-table/expired-table.component';
import { AbortedTableComponent } from '../../components/table/aborted-table/aborted-table.component';
import { Spinner } from '../../components/spinner/spinner.component';
import { asyncHandler } from '../../utils/async-handler';
import { DeleteButton } from '../../components/button/delete-pubs-button';
import { Currency } from '../../interfaces/publication.interface';
import { SUSPENDED_MODAL_CONTENT, UserStatus } from '../../constants';
import ModalComponent from '../../components/modal/modal.component';

// Helper para errores de fetch
const getFetchError = (...queries: any[]) => {
	return queries.find((q) => q.isError);
};

const getErrorMessage = (query: any) => {
	if (!query || !query.error) return 'Ocurrió un error inesperado.';
	if (typeof query.error === 'string') return query.error;
	if (query.error.message) return query.error.message;
	return 'Ocurrió un error inesperado.';
};

// Helpers para cantidad y renderizado
const getPublicationCount = (pubs: {
	isLoading: boolean;
	isSuccess: boolean;
	data?: any[];
}) => (pubs.isLoading ? '...' : pubs.isSuccess ? pubs.data?.length ?? 0 : 0);

const renderPublicationList = (
	pubs: { data?: any[] },
	currency: Currency,
	Component: any,
) =>
	pubs.data && pubs.data.length > 0 ? (
		<List sx={{ display: 'flex', flexDirection: 'column', rowGap: '10px' }}>
			{pubs.data.map((publication: any) => (
				<Component key={publication.id} currency={currency}>
					{publication}
				</Component>
			))}
		</List>
	) : null;

const SellingDashboard = () => {
	const navigate = useNavigate();
	const [isCompletedDialogOpen, setCompletedDialogOpen] = useState(false);
	const [isExpiredDialogOpen, setExpiredDialogOpen] = useState(false);
	const [pubsToDelete, setPubsToDelete] = useState<number[]>();
	const [currencyColor, setCurrencyColor] = useState('#48887B');
	const {
		currency,
		setCurrency,
		sellPublications,
		confirmedSellPublications,
		completedSellPublications,
		expiredSellPublications,
		abortedSellPublications,
	} = useContext(CambiappContext) as CambiappContextType;

	// Unificación de loading
	const isLoading =
		sellPublications.isLoading ||
		confirmedSellPublications.isLoading ||
		completedSellPublications.isLoading ||
		expiredSellPublications.isLoading ||
		abortedSellPublications.isLoading;

	// Manejo de errores
	const fetchError = getFetchError(
		sellPublications,
		confirmedSellPublications,
		completedSellPublications,
		expiredSellPublications,
		abortedSellPublications,
	);

	const handleChange = (event: SelectChangeEvent) => {
		const currency = event.target.value as Currency;
		setCurrency(currency);
		switch (currency) {
			case Currency.USD:
				setCurrencyColor('#48887B');
				break;
			case Currency.EUR:
				setCurrencyColor('#384881');
				break;
			case Currency.BRL:
				setCurrencyColor('#DA7739');
				break;
		}
	};

	const amountOfPublications = getPublicationCount(sellPublications);
	const amountOfConfirmedPublications = getPublicationCount(
		confirmedSellPublications,
	);
	const amountOfCompletedPublications = getPublicationCount(
		completedSellPublications,
	);

	// Refetch inicial y cuando cambia la moneda
	useEffect(() => {
		sellPublications.refetch();
		confirmedSellPublications.refetch();
		completedSellPublications.refetch();
		expiredSellPublications.refetch();
		abortedSellPublications.refetch();
	}, [currency]);

	theme.palette.background.default = '#d2d2d2';

	const handleDialogOfCompletedPublications = () => {
		setPubsToDelete(completedSellPublications.data?.map((pub) => pub.id));
		setCompletedDialogOpen(true);
	};
	const handleDialogOfExpiredPublications = () => {
		setPubsToDelete([
			...expiredSellPublications.data?.map((pub) => pub.id)!,
			...abortedSellPublications.data?.map((pub) => pub.id)!,
		]);
		setExpiredDialogOpen(true);
	};
	const handleHideOfCompletedPublications = () => {
		asyncHandler.hidePublications(pubsToDelete!, () =>
			setCompletedDialogOpen(false),
		);
	};
	const handleHideOfExpiredPublications = () => {
		asyncHandler.hidePublications(pubsToDelete!, () =>
			setExpiredDialogOpen(false),
		);
	};

	const { userStatus } = useContext(CambiappContext) as CambiappContextType;

	const [modalOpen, setModalOpen] = useState(false);

	useEffect(() => {
		if (userStatus === UserStatus.SUSPENDED) {
			setModalOpen(true);
		}
	}, [userStatus]);

	const handleModalClose = () => {
		setModalOpen(false);
	};

	const styles = {
		fab: {
			position: 'fixed',
			bottom: '5%',
			left: '50%',
			transform: 'translateX(-50%)',
			minWidth: '200px',
			width: '50%',
			maxWidth: '300px',
			minHeight: '50px',
			backgroundColor: currencyColor,
			borderRadius: '5px',
			color: 'white',
		},
		disabled: {
			'&.Mui-disabled': {
				backgroundColor: '#ccc',
				color: '#666',
			},
		},
	};

	return (
		<Box
			sx={{ backgroundColor: '#F1F1F2', height: '100%', minHeight: '100vh' }}
		>
			{isLoading && (
				<Box
					sx={{
						position: 'absolute',
						top: '57px',
						backgroundColor: '#00000033',
						width: '100vw',
						height: 'calc(100vh - 57px)',
						zIndex: 2000,
					}}
				>
					<Spinner />
				</Box>
			)}
			{fetchError && (
				<Alert
					severity="error"
					sx={{ m: 2 }}
					action={
						<Button
							color="inherit"
							size="small"
							onClick={() => {
								sellPublications.refetch();
								confirmedSellPublications.refetch();
								completedSellPublications.refetch();
								expiredSellPublications.refetch();
								abortedSellPublications.refetch();
							}}
						>
							Reintentar
						</Button>
					}
				>
					{getErrorMessage(fetchError)}
				</Alert>
			)}
			<CssBaseline enableColorScheme />
			<Box
				sx={{
					backgroundColor: currencyColor,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					padding: '10px 15px',
				}}
			>
				<Box sx={{ flex: 1 }} />
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						flex: 2,
					}}
				>
					<Typography
						fontWeight={600}
						fontSize={20}
						sx={{ marginRight: '10px' }}
					>
						{currency === Currency.USD && 'Dólares'}
						{currency === Currency.EUR && 'Euros'}
						{currency === Currency.BRL && 'Reales'}
					</Typography>
					<img
						src={
							currency === Currency.USD
								? 'usa-flag.png'
								: currency === Currency.EUR
								? 'euro-flag.png'
								: 'real-flag.png'
						}
						alt="Logo"
						style={{ border: '1px solid white', borderRadius: 100 }}
						width={23}
					/>
				</Box>
				<FormControl
					sx={{
						flex: 1,
						maxWidth: 110,
						backgroundColor: '#CCCCCC',
						borderRadius: '5px',
					}}
					size="small"
				>
					<Select
						id="demo-simple-select"
						value={currency}
						onChange={handleChange}
						sx={{
							'color': 'black',
							'.MuiOutlinedInput-notchedOutline': {
								borderColor: '#191919',
							},
							'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
								borderColor: '#191919',
							},
							'&:hover .MuiOutlinedInput-notchedOutline': {
								borderColor: '#191919',
							},
							'.MuiSvgIcon-root ': {
								fill: 'black !important',
							},
						}}
					>
						<MenuItem value={Currency.USD}>USD</MenuItem>
						<MenuItem value={Currency.EUR}>EUR</MenuItem>
						<MenuItem value={Currency.BRL}>BRL</MenuItem>
					</Select>
				</FormControl>
			</Box>
			<Accordion defaultExpanded>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon sx={{ color: 'black' }} />}
					sx={{ backgroundColor: '#CCCCCC', color: 'black' }}
				>
					<Typography fontWeight={400} fontSize={16}>
						Publicaciones vigentes{' '}
						<span style={{ color: '#737373' }}>{amountOfPublications}</span>
					</Typography>
				</AccordionSummary>
				{sellPublications.data && sellPublications.data.length > 0 && (
					<AccordionDetails
						sx={{ backgroundColor: '#d2d2d2', padding: '0px 5px' }}
					>
						{renderPublicationList(sellPublications, currency, Publication)}
					</AccordionDetails>
				)}
			</Accordion>
			<Accordion defaultExpanded>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon sx={{ color: 'black' }} />}
					sx={{ backgroundColor: '#CCCCCC', color: 'black' }}
				>
					<Typography fontWeight={400} fontSize={16}>
						Operaciones confirmadas{' '}
						<span style={{ color: '#737373' }}>
							{amountOfConfirmedPublications}
						</span>
					</Typography>
				</AccordionSummary>
				{confirmedSellPublications.data &&
					confirmedSellPublications.data.length > 0 && (
						<AccordionDetails
							sx={{ backgroundColor: '#d2d2d2', padding: '0px 5px' }}
						>
							{renderPublicationList(
								confirmedSellPublications,
								currency,
								ConfirmedPublication,
							)}
						</AccordionDetails>
					)}
			</Accordion>
			<Accordion>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon sx={{ color: 'black' }} />}
					sx={{ backgroundColor: '#CCCCCC', color: 'black' }}
				>
					<Typography fontWeight={400} fontSize={16}>
						Operaciones concretadas{' '}
						<span style={{ color: '#737373' }}>
							{amountOfCompletedPublications}
						</span>
					</Typography>
				</AccordionSummary>
				{completedSellPublications.data &&
					completedSellPublications.data.length > 0 && (
						<DeleteButton
							isHideDialogOpen={isCompletedDialogOpen}
							setHideDialogOpen={setCompletedDialogOpen}
							buttonAction={handleDialogOfCompletedPublications}
							buttonCallback={handleHideOfCompletedPublications}
						/>
					)}
				{completedSellPublications.data &&
					completedSellPublications.data.length > 0 && (
						<Table
							tableHeader={completedPublicationsTableHeader}
							tableBody={completedSellPublications.data!}
						/>
					)}
			</Accordion>
			<Accordion>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon sx={{ color: 'black' }} />}
					sx={{ backgroundColor: '#CCCCCC', color: 'black' }}
				>
					<Typography fontWeight={400} fontSize={16}>
						Publicaciones y Op. Archivadas{' '}
						<span style={{ color: '#737373' }}>
							{(expiredSellPublications.data?.length || 0) +
								(abortedSellPublications.data?.length || 0)}
						</span>
					</Typography>
				</AccordionSummary>
				{((expiredSellPublications.data &&
					expiredSellPublications.data.length > 0) ||
					(abortedSellPublications.data &&
						abortedSellPublications.data.length > 0)) && (
					<DeleteButton
						isHideDialogOpen={isExpiredDialogOpen}
						setHideDialogOpen={setExpiredDialogOpen}
						buttonAction={handleDialogOfExpiredPublications}
						buttonCallback={handleHideOfExpiredPublications}
					/>
				)}
				<Typography
					align="center"
					sx={{ backgroundColor: '#D4ECF9', color: 'black' }}
				>
					Publicaciones vencidas/caducadas
				</Typography>
				{expiredSellPublications.data && (
					<ExpiredTableComponent
						expiredPublications={expiredSellPublications.data}
					/>
				)}
				<Typography
					align="center"
					sx={{ backgroundColor: '#D4ECF9', color: 'black' }}
				>
					Publicaciones abortadas/eliminadas
				</Typography>
				{abortedSellPublications.data && (
					<AbortedTableComponent
						abortedPublications={abortedSellPublications.data}
					/>
				)}
			</Accordion>
			<Fab
				variant="extended"
				size="medium"
				aria-label="add"
				sx={{ ...styles.fab, ...styles.disabled }}
				onClick={() => {
					navigate('new');
				}}
				disabled={userStatus === UserStatus.SUSPENDED}
			>
				<Box sx={{ width: '100%' }}>
					<Typography fontSize={14} lineHeight={1.25} fontWeight={600}>
						{`Nueva publicación de venta ${currency}`}
					</Typography>
				</Box>
				<AddIcon />
			</Fab>
			<ModalComponent
				open={modalOpen}
				title={SUSPENDED_MODAL_CONTENT.title}
				description={SUSPENDED_MODAL_CONTENT.description}
				imageSrc="/cambiapp_logo.png"
				onClose={handleModalClose}
				buttonText="Cerrar"
				onButtonClick={handleModalClose}
				showCloseButton
			/>
		</Box>
	);
};

export default SellingDashboard;
